import React, { useEffect, useState } from "react";

import {
  Box,
  ButtonBase,
  Dialog,
  InputBase,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { Tick } from "@dashboard/components/common/settingsRows";
import {
  focusRing,
  reducedMotion,
} from "@dashboard/components/themes/themeMotion";

import { SheetButton } from "./SheetButton";
import {
  REASON_DETAIL_MAX,
  SWITCH_BACK_REASONS,
  type SwitchBackReason,
} from "./guestMenuEvents";
import { SHEET_TRANSITION_MS, sheetBackdrop, sheetPaper } from "./sheetStyles";

const COPY = {
  switch_back: {
    title: "Switch back to Classic?",
    body: "Guests will see Classic straight away. What made you switch? It helps us fix it.",
    confirm: "Switch to Classic",
  },
  keep_classic: {
    title: "Keep Classic for now?",
    body: "Your guests keep seeing Classic. What's holding you back? It helps us fix it.",
    confirm: "Keep Classic",
  },
} as const;

type SwitchBackSheetProps = {
  open: boolean;
  /** From the Menus notice, before the restaurant has switched. */
  purpose?: keyof typeof COPY;
  onCancel: (reason: SwitchBackReason | null) => void;
  onConfirm: (reason: SwitchBackReason | null, detail: string) => void;
};

/**
 * Asks why, once, as a restaurant goes back to Classic. Answering is optional
 * and never stands in the way of switching.
 */
export const SwitchBackSheet = ({
  open,
  purpose = "switch_back",
  onCancel,
  onConfirm,
}: SwitchBackSheetProps) => {
  const copy = COPY[purpose];
  const { palette } = useTheme();
  const oscar = palette.oscar.light;
  const [reason, setReason] = useState<SwitchBackReason | null>(null);
  const [detail, setDetail] = useState("");

  useEffect(() => {
    if (open) {
      setReason(null);
      setDetail("");
    }
  }, [open]);

  const detailShown = reason === "other";

  return (
    <Dialog
      open={open}
      onClose={() => onCancel(reason)}
      aria-labelledby="switch-back-title"
      transitionDuration={SHEET_TRANSITION_MS}
      PaperProps={{
        sx: [
          sheetPaper,
          {
            width: 460,
            p: 3.5,
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 2.25,
            "@keyframes switchBackIn": {
              from: { transform: "translateY(12px)" },
              to: { transform: "translateY(0)" },
            },
            animation: "switchBackIn 250ms ease-out",
          },
          reducedMotion,
        ],
      }}
      slotProps={{
        backdrop: { sx: sheetBackdrop },
      }}
    >
      <Stack gap={0.75}>
        <Typography
          id="switch-back-title"
          variant="labelLMedium"
          sx={{ fontSize: 20, lineHeight: "28px" }}
        >
          {copy.title}
        </Typography>
        <Typography
          variant="bodyRRegular"
          sx={{ color: oscar.content.content2Text }}
        >
          {copy.body}
        </Typography>
      </Stack>

      <Box
        role="radiogroup"
        aria-label="Reason"
        sx={{
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${oscar.borders.gray}`,
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {SWITCH_BACK_REASONS.map((option, index) => {
          const checked = option.value === reason;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={checked}
              onClick={() => setReason(option.value)}
              sx={[
                {
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  textAlign: "left",
                  borderTop:
                    index === 0 ? 0 : `1px solid ${oscar.borders.gray}`,
                  background: checked
                    ? oscar.surface.takeNote
                    : oscar.surface.clear,
                  transition: "background 150ms ease-out",
                },
                focusRing(-2),
                reducedMotion,
              ]}
            >
              <Typography variant="bodyRRegular">{option.label}</Typography>
              <Tick shown={checked} color={oscar.borders.electric} />
            </ButtonBase>
          );
        })}
      </Box>

      <Box
        aria-hidden={!detailShown}
        sx={[
          {
            overflow: "hidden",
            maxHeight: detailShown ? 140 : 0,
            opacity: detailShown ? 1 : 0,
            // The sheet's gap goes with the box.
            mt: detailShown ? 0 : -2.25,
            visibility: detailShown ? "visible" : "hidden",
            transition:
              "max-height 250ms ease-in-out, opacity 200ms ease-out, margin-top 250ms ease-in-out, visibility 250ms",
          },
          reducedMotion,
        ]}
      >
        <Typography
          component="label"
          htmlFor="switch-back-detail"
          variant="bodyS"
          sx={{
            display: "block",
            mb: 0.75,
            fontSize: 13,
            color: oscar.content.content2Text,
          }}
        >
          Tell us more (optional)
        </Typography>
        <InputBase
          id="switch-back-detail"
          multiline
          minRows={3}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          inputProps={{ maxLength: REASON_DETAIL_MAX }}
          sx={{
            width: "100%",
            border: `1px solid ${oscar.borders.gray}`,
            borderRadius: "8px",
            px: 1.5,
            py: 1.25,
            fontSize: 14,
          }}
        />
      </Box>

      <Stack direction="row" justifyContent="flex-end" gap={1.25}>
        <SheetButton onClick={() => onCancel(reason)}>Cancel</SheetButton>
        <SheetButton primary onClick={() => onConfirm(reason, detail)}>
          {copy.confirm}
        </SheetButton>
      </Stack>
    </Dialog>
  );
};
